/**
 * Cost Calculator - Single Source of Truth
 * 
 * This calculator provides ALL cost-related calculations for the inventory system.
 * It replaces multiple scattered cost calculation implementations.
 * 
 * Key Features:
 * - Recipe cost calculation with ingredient, labor, and overhead
 * - Cost of Goods Sold (COGS) calculation
 * - Profit margin calculations
 * - Stock turnover rate
 * - Cost variance analysis
 * 
 * @module inventory/calculators/CostCalculator
 */

import type {
  Material,
  Recipe,
  RecipeIngredient,
  Order,
  Expense,
  ExpenseCategory,
  RecipeCostBreakdown,
  CostCalculationResult,
  RecipeCostData,
  BranchExpense,
  UUID,
} from '../types';

export interface CostCalculatorConfig {
  default_labor_cost_percentage: number; // Default 15%
  default_overhead_percentage: number;   // Default 10%
  currency: string;
}

export class CostCalculator {
  private config: CostCalculatorConfig;

  constructor(config?: Partial<CostCalculatorConfig>) {
    this.config = {
      default_labor_cost_percentage: config?.default_labor_cost_percentage ?? 15,
      default_overhead_percentage: config?.default_overhead_percentage ?? 10,
      currency: config?.currency ?? 'EGP',
    };
  }

  /**
   * Calculate the total cost of a recipe
   * 
   * Formula:
   * Total Cost = Ingredients Cost + Labor Cost + Overhead Cost
   * 
   * Where:
   * - Ingredients Cost = Σ(ingredient.quantity × material.cost_per_unit)
   * - Labor Cost = Ingredients Cost × labor_percentage
   * - Overhead Cost = Ingredients Cost × overhead_percentage
   * 
   * @param recipe - Recipe with ingredients
   * @param materials - Available materials with costs
   * @param expenses - Optional expenses to calculate actual overhead
   * @returns Cost breakdown with all components
   */
  calculateRecipeCost(
    recipe: Recipe & { ingredients: RecipeIngredient[] },
    materials: Material[],
    expenses?: Expense[]
  ): CostCalculationResult {
    // Calculate ingredient costs
    const ingredientsCost = recipe.ingredients.reduce((sum, ingredient) => {
      const material = materials.find(m => m.id === ingredient.material_id);
      if (!material) {
        console.warn(`Material ${ingredient.material_id} not found for recipe ${recipe.id}`);
        return sum;
      }
      return sum + (ingredient.quantity * material.cost_per_unit);
    }, 0);

    // Calculate labor cost
    // If prep_time is available, use time-based calculation
    // Otherwise, use percentage of ingredients cost
    const laborCost = recipe.prep_time
      ? this.calculateLaborCostByTime(recipe.prep_time)
      : ingredientsCost * (this.config.default_labor_cost_percentage / 100);

    // Calculate overhead cost
    const overheadCost = expenses
      ? this.calculateActualOverhead(expenses, ingredientsCost)
      : ingredientsCost * (this.config.default_overhead_percentage / 100);

    const totalCost = ingredientsCost + laborCost + overheadCost;
    const costPerPortion = recipe.portion_size > 0 
      ? totalCost / recipe.portion_size 
      : totalCost;

    // Calculate percentages
    const breakdown: RecipeCostBreakdown = {
      ingredients_cost: ingredientsCost,
      ingredients_percentage: totalCost > 0 ? (ingredientsCost / totalCost) * 100 : 0,
      labor_cost: laborCost,
      labor_percentage: totalCost > 0 ? (laborCost / totalCost) * 100 : 0,
      overhead_cost: overheadCost,
      overhead_percentage: totalCost > 0 ? (overheadCost / totalCost) * 100 : 0,
      total_cost: totalCost,
    };

    return {
      recipe_id: recipe.id,
      total_cost: totalCost,
      cost_per_portion: costPerPortion,
      breakdown,
    };
  }

  /**
   * Calculate labor cost based on preparation time
   * 
   * @param prepTimeMinutes - Preparation time in minutes
   * @param hourlyRate - Hourly labor rate (default based on region)
   * @returns Labor cost
   */
  private calculateLaborCostByTime(prepTimeMinutes: number, hourlyRate: number = 50): number {
    return (prepTimeMinutes / 60) * hourlyRate;
  }

  /**
   * Calculate actual overhead from expenses
   * 
   * @param expenses - All expenses in the period
   * @param ingredientsCost - Base cost for overhead calculation
   * @returns Calculated overhead cost
   */
  private calculateActualOverhead(expenses: Expense[], ingredientsCost: number): number {
    // Sum all overhead expenses (rent, utilities, etc.)
    // For now, use default percentage - will be enhanced later
    return ingredientsCost * (this.config.default_overhead_percentage / 100);
  }

  /**
   * Calculate Cost of Goods Sold (COGS)
   * 
   * COGS = Total cost of all items sold in the period
   * Formula: Σ(order_item.quantity × recipe.total_cost)
   * 
   * This is the CORRECT way to calculate COGS - not from inventory deductions!
   * 
   * @param orders - Orders in the period
   * @param recipes - Recipe definitions
   * @param materials - Materials with costs
   * @returns Total COGS for the period
   */
  calculateCOGS(
    orders: Order[],
    recipes: (Recipe & { ingredients: RecipeIngredient[] })[],
    materials: Material[]
  ): number {
    let totalCOGS = 0;

    for (const order of orders) {
      for (const orderItem of order.order_items) {
        const recipe = recipes.find(r => r.menu_item_id === orderItem.menu_item_id);
        if (!recipe) {
          console.warn(`Recipe not found for menu item ${orderItem.menu_item_id}`);
          continue;
        }

        const recipeCost = this.calculateRecipeCost(recipe, materials);
        totalCOGS += recipeCost.total_cost * orderItem.quantity;
      }
    }

    return totalCOGS;
  }

  /**
   * Calculate stock turnover rate
   * 
   * Formula: Stock Turnover Rate = COGS / Average Inventory Value
   * 
   * Where Average Inventory = (Opening Stock + Closing Stock) / 2
   * 
   * This is the CORRECT formula - not quantity/value!
   * 
   * @param cogs - Cost of Goods Sold for the period
   * @param openingStockValue - Value at start of period
   * @param closingStockValue - Value at end of period
   * @returns Turnover rate (times per period)
   */
  calculateStockTurnoverRate(
    cogs: number,
    openingStockValue: number,
    closingStockValue: number
  ): number {
    const averageInventoryValue = (openingStockValue + closingStockValue) / 2;
    
    if (averageInventoryValue === 0) {
      return 0;
    }

    return cogs / averageInventoryValue;
  }

  /**
   * Calculate days of inventory on hand
   * 
   * Formula: Days = 365 / Stock Turnover Rate
   * 
   * @param stockTurnoverRate - Annual turnover rate
   * @returns Average days of inventory
   */
  calculateDaysOfInventory(stockTurnoverRate: number): number {
    if (stockTurnoverRate === 0) {
      return 0;
    }
    return 365 / stockTurnoverRate;
  }

  /**
   * Calculate profit margin for a recipe
   * 
   * @param sellingPrice - Menu item selling price
   * @param totalCost - Recipe total cost
   * @returns Profit margin amount and percentage
   */
  calculateProfitMargin(sellingPrice: number, totalCost: number): {
    profit_margin: number;
    profit_margin_percentage: number;
  } {
    const profitMargin = sellingPrice - totalCost;
    const profitMarginPercentage = sellingPrice > 0 
      ? (profitMargin / sellingPrice) * 100 
      : 0;

    return {
      profit_margin: profitMargin,
      profit_margin_percentage: profitMarginPercentage,
    };
  }

  /**
   * Calculate comprehensive recipe cost data including profitability
   * 
   * @param recipe - Recipe with ingredients
   * @param materials - Materials with costs
   * @param sellingPrice - Menu item price
   * @param salesCount - Number of times sold (for efficiency score)
   * @returns Complete cost analysis for the recipe
   */
  calculateRecipeCostData(
    recipe: Recipe & { ingredients: RecipeIngredient[] },
    materials: Material[],
    sellingPrice: number,
    salesCount: number = 0
  ): Omit<RecipeCostData, 'menu_item_name' | 'menu_item_id'> {
    const costResult = this.calculateRecipeCost(recipe, materials);
    const { profit_margin, profit_margin_percentage } = this.calculateProfitMargin(
      sellingPrice,
      costResult.total_cost
    );

    // Calculate efficiency score (0-100)
    // Based on: profit margin (70%) + sales performance (30%)
    const marginScore = Math.min(profit_margin_percentage * 0.7, 70);
    const salesScore = Math.min((salesCount / 10) * 30, 30); // Max at 10 sales
    const efficiencyScore = Math.max(0, Math.min(100, marginScore + salesScore));

    // Generate recommendations based on analysis
    const recommendations = this.generateRecommendations(
      profit_margin_percentage,
      costResult.breakdown,
      salesCount
    );

    return {
      recipe_id: recipe.id,
      recipe_name: recipe.name,
      ingredients: recipe.ingredients.map(ing => {
        const material = materials.find(m => m.id === ing.material_id);
        return {
          ...ing,
          material_name: material?.name || 'Unknown',
          cost_per_unit: material?.cost_per_unit || 0,
          total_cost: ing.quantity * (material?.cost_per_unit || 0),
        };
      }),
      cost_breakdown: costResult.breakdown,
      selling_price: sellingPrice,
      profit_margin,
      profit_margin_percentage,
      efficiency_score: efficiencyScore,
      recommendations,
    };
  }

  /**
   * Generate actionable recommendations based on cost analysis
   */
  private generateRecommendations(
    profitMarginPercentage: number,
    breakdown: RecipeCostBreakdown,
    salesCount: number
  ): string[] {
    const recommendations: string[] = [];

    // Profit margin recommendations
    if (profitMarginPercentage < 20) {
      recommendations.push('⚠️ Low profit margin. Consider increasing price or reducing costs.');
    } else if (profitMarginPercentage > 70) {
      recommendations.push('💡 High profit margin. Opportunity to reduce price and increase volume.');
    } else {
      recommendations.push('✅ Healthy profit margin.');
    }

    // Cost structure recommendations
    if (breakdown.labor_percentage > 25) {
      recommendations.push('⏱️ Labor costs are high. Consider streamlining preparation process.');
    }

    if (breakdown.overhead_percentage > 20) {
      recommendations.push('💰 Overhead costs are high. Review operational expenses.');
    }

    // Sales performance
    if (salesCount === 0) {
      recommendations.push('📊 No sales recorded. Consider promotion or menu positioning.');
    } else if (salesCount > 50) {
      recommendations.push('🌟 Popular item! Monitor ingredient availability closely.');
    }

    return recommendations;
  }

  /**
   * Calculate cost variance (standard deviation) across materials
   * 
   * Useful for understanding cost consistency
   * 
   * @param materials - Materials to analyze
   * @returns Standard deviation of costs
   */
  calculateCostVariance(materials: Material[]): number {
    if (materials.length === 0) return 0;

    const costs = materials.map(m => m.cost_per_unit);
    const mean = costs.reduce((sum, cost) => sum + cost, 0) / costs.length;
    const variance = costs.reduce((sum, cost) => sum + Math.pow(cost - mean, 2), 0) / costs.length;
    
    return Math.sqrt(variance);
  }

  /**
   * Calculate average cost per unit across all inventory
   * 
   * @param materials - All materials
   * @returns Average cost weighted by stock quantity
   */
  calculateAverageCost(materials: Material[]): number {
    const totalValue = materials.reduce((sum, m) => sum + (m.current_stock * m.cost_per_unit), 0);
    const totalUnits = materials.reduce((sum, m) => sum + m.current_stock, 0);
    
    return totalUnits > 0 ? totalValue / totalUnits : 0;
  }

  /**
   * Allocate expenses to menu items based on revenue contribution
   * 
   * @param expenses - Expenses to allocate
   * @param menuItemRevenues - Map of menu_item_id to revenue
   * @returns Map of menu_item_id to allocated expense amount
   */
  allocateExpensesToItems(
    expenses: (Expense & { category?: ExpenseCategory; branch_expenses: BranchExpense[] })[],
    menuItemRevenues: Map<UUID, number>
  ): Map<UUID, number> {
    const allocation = new Map<UUID, number>();
    
    // Calculate total revenue
    const totalRevenue = Array.from(menuItemRevenues.values())
      .reduce((sum, revenue) => sum + revenue, 0);
    
    if (totalRevenue === 0) return allocation;

    // Get allocable expenses (per_order type)
    const allocableExpenses = expenses.filter(
      e => e.category?.allocation_type === 'per_order'
    );
    
    const totalExpenses = allocableExpenses.reduce((sum, e) => {
      return sum + e.branch_expenses.reduce((s, be) => s + be.allocated_amount, 0);
    }, 0);

    // Allocate based on revenue percentage
    menuItemRevenues.forEach((revenue, menuItemId) => {
      const revenuePercentage = revenue / totalRevenue;
      const allocatedAmount = totalExpenses * revenuePercentage;
      allocation.set(menuItemId, allocatedAmount);
    });

    return allocation;
  }
}

// Export singleton instance with default configuration
export const costCalculator = new CostCalculator();
